import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/Table';
import { TriangleBoth } from '../ui/SortIcons';
import { fetchClaimDetails } from '../../services/referralService';
import { LoadingSpinner } from '../LoadingSpinner';

// Display order matches the user-confirmed Claim Detail report layout. Each
// entry maps an API field name to the column header rendered in the table.
// Keeping this as a single source of truth means adding/reordering columns is
// a one-line change.
const COLUMNS = [
  { key: 'claim_id', label: 'Claim ID' },
  { key: 'prov_tin', label: 'Billing TIN' },
  { key: 'pat_id', label: 'Member ID' },
  { key: 'pat_first_name', label: 'First Name' },
  { key: 'pat_last_name', label: 'Last Name' },
];

// Eight asterisks matches the legacy myDBR convention in
// sp_DBR_referral_review_clm_list_view ('dbr.colstyle','TPA Clm Id','********').
// Hard-coded — do not parameterize.
const PHI_MASK = '********';

function renderCell(value, isPhiMasked) {
  // PHI-masked wins over the null branch. The backend sends null AND adds
  // the field to phi_masked_fields when the user lacks PHI access; em-dash
  // remains reserved for genuinely empty data.
  if (isPhiMasked) return PHI_MASK;
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return String(value);
}

function ClaimDetailTab({ refId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!refId) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    fetchClaimDetails(refId)
      .then((response) => {
        if (!cancelled) {
          setRows(Array.isArray(response?.results) ? response.results : []);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [refId]);

  // Singular/plural copy for the footer record count.
  const recordCountLabel = rows.length === 1 ? '1 record' : `${rows.length} records`;

  return (
    // Outer padding gives the table card breathing room from the drawer edges,
    // matching the visual treatment in the design mockup.
    <div className="px-6 py-4 flex flex-col min-h-0 flex-1">
      <div className="flex flex-col min-h-0 flex-1 border border-border rounded-md overflow-hidden bg-background">
        {/*
          Single overflow-auto container so the sticky <thead> resolves its
          scroll context against this element (nested vertical + horizontal
          scroll containers break sticky positioning).
        */}
        <div className="flex-1 min-h-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted">
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    className="h-8 px-3 font-semibold text-xs whitespace-nowrap text-foreground align-middle"
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      {/*
                        Decorative sort icon only — no click handler, no state.
                        Matches the referral table's visual style for now;
                        functional sorting will be wired up in a later ticket.
                      */}
                      <TriangleBoth className="h-3 w-3 opacity-[0.35]" />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-8 text-center text-sm text-muted-foreground"
                  >
                    <LoadingSpinner label="Loading claim details" className="mx-auto" />
                  </TableCell>
                </TableRow>
              )}

              {!loading && error && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    className="py-8 text-center text-sm text-destructive"
                    data-testid="claim-detail-error"
                  >
                    Unable to load claim detail.
                  </TableCell>
                </TableRow>
              )}

              {!loading && !error && rows.map((row, idx) => (
                <TableRow
                  key={`claim-row-${idx}`}
                >
                  {COLUMNS.map((col) => {
                    // PhiMaskingMixin omits phi_masked_fields entirely when
                    // the user has PHI access — the Array.isArray guard keeps
                    // this safe against undefined access on those rows.
                    const isMasked =
                      Array.isArray(row.phi_masked_fields) &&
                      row.phi_masked_fields.includes(col.key);
                    return (
                      <TableCell
                        key={col.key}
                        className="px-3 py-1 text-xs text-left text-foreground align-middle whitespace-nowrap"
                      >
                        {renderCell(row[col.key], isMasked)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/*
          Footer anchors to the bottom of the flex column. The record count
          doubles as the empty-state indicator ("0 records") — no separate
          empty message in the table body. Download stays disabled until the
          export endpoint exists; no onClick handler.
        */}
        <div className="flex items-center justify-center gap-6 border-t border-border bg-muted/30 px-4 py-2 text-xs">
          <span className="text-muted-foreground">{recordCountLabel}</span>
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
}

export { ClaimDetailTab };
